import type { DataSource } from "../types/data.js";
import type { ReportTemplate } from "../types/template.js";

/**
 * The contract between BroadPaper and whatever model a host chooses.
 *
 * Nothing in here names a provider, and nothing in `@broadpaper/core/ai` makes
 * a network call: the host's `generate` does that, wherever and however it
 * likes, and BroadPaper treats what comes back as untrusted input. Every type
 * is plain data so a request can cross from a browser to the host's own back
 * end as JSON — which is where the key and the cost belong.
 */

export type TemplateAiMode = "create" | "update";

/** What the model is told the designer offers. Built from a block registry by `describeDesigner`. */
export interface TemplateAiContext {
  blocks: TemplateAiBlock[];
  /** Theme tokens a colour or font may name, such as `$colors.primary`. */
  themeTokens: Array<{ token: string; value: string }>;
  /** Document text style ids: `h1`, `body`, `caption`… */
  textStyles: string[];
}

export interface TemplateAiBlock {
  type: string;
  name: string;
  description?: string;
  category: string;
  /** Props a new block of this type starts with — the shape the model should follow. */
  defaultProps: Record<string, unknown>;
  /** Every prop name the block reads. A proposal naming any other is refused. */
  props: string[];
  /** Props holding expressions, and what kind. */
  bindings: Array<{ prop: string; kind: string }>;
  /** Block types or groups it may contain, when it is a container. */
  accepts?: string[];
  /** Allowed values for select-style props. */
  options?: Record<string, Array<string | number | boolean>>;
}

export interface TemplateAiRequest {
  /**
   * Chosen by the designer, one per ask (repairs keep it). Hosts that meter or
   * audit key the attempt by it, and `decided` reports the outcome against it.
   */
  requestId: string;
  mode: TemplateAiMode;
  /** What the person typed. At most `INSTRUCTION_LIMIT` characters. */
  instruction: string;
  /**
   * The host's data sources with every `example` removed. Field names, types,
   * labels and descriptions only — never a value from anybody's data.
   */
  dataModel: DataSource[];
  context: TemplateAiContext;
  /**
   * The design as it stands. Always the whole of it, even with a selection:
   * a block edited without the page, styles and siblings around it is how a
   * change comes back that no longer fits where it goes. Embedded images are
   * replaced by `asset:N` placeholders before it is sent.
   */
  currentTemplate?: ReportTemplate;
  /** Node ids the person had selected. Changes must stay inside them. */
  selection?: string[];
  /** BCP-47, for the language of any wording the model writes. */
  locale?: string;
  /** Present on a second or third attempt: what was wrong with the last one. */
  repair?: TemplateAiRepair;
}

export interface TemplateAiRepair {
  attempt: 1 | 2;
  previousOutput: unknown;
  problems: string[];
}

export type TemplateAiResult =
  | { status: "proposed"; requestId: string; template: ReportTemplate; notes?: string }
  | { status: "refused"; requestId: string; reason: string };

/** The allowance a host wants shown. A fraction and words, never money. */
export interface TemplateAiAllowance {
  /** 0..1 of the allowance used. */
  used: number;
  /** For example "approx. 7 generations left". */
  label: string;
}

/**
 * What a host passes as the designer's `ai` option.
 *
 * `generate` returns the model's raw tool input — the object it was asked to
 * produce — and BroadPaper checks it and, when it fails, calls `generate` again
 * with `request.repair` set. It may also throw a `TemplateAiError` to say why
 * nothing came back.
 */
export interface TemplateAiProvider {
  generate(request: TemplateAiRequest, signal: AbortSignal): Promise<unknown>;
  /** Shown as a bar under the instruction box. Omit to show none. */
  allowance?(): Promise<TemplateAiAllowance | null>;
  /** Told whether the person applied or discarded what was proposed. */
  decided?(requestId: string, decision: "applied" | "rejected"): void;
  /**
   * How many times BroadPaper may ask again after a failed check, 0–2 (default 2).
   * A host that already repairs on its own server — BroadPaper Cloud does, so
   * repairs are metered where the money is — sets 0.
   */
  maxRepairs?: number;
}

export type TemplateAiErrorKind =
  /** The model declined: the request was not about designing a template. */
  | "refused"
  /** What came back could not be made to pass validation. */
  | "invalid"
  /** The host's allowance is spent. */
  | "allowance"
  /** Too many requests; `retryAfter` says how long. */
  | "rate-limited"
  /** The host or its provider is not answering, or AI is switched off. */
  | "unavailable"
  /** The person cancelled. */
  | "cancelled"
  | "failed";

/**
 * A failure a person can be shown. `message` is written for them; nothing from
 * a provider's own error goes in it.
 */
export class TemplateAiError extends Error {
  readonly kind: TemplateAiErrorKind;
  readonly hint?: string;
  /** A link to offer — an upgrade, a help page. */
  readonly action?: { label: string; href: string };
  readonly retryAfter?: number;

  constructor(kind: TemplateAiErrorKind, message: string, extra: { hint?: string; action?: { label: string; href: string }; retryAfter?: number } = {}) {
    super(message);
    this.name = "TemplateAiError";
    this.kind = kind;
    this.hint = extra.hint;
    this.action = extra.action;
    this.retryAfter = extra.retryAfter;
  }
}

export const INSTRUCTION_LIMIT = 2000;
export const MAX_REPAIRS = 2;
export const REFUSAL_MESSAGE = "I can only help design BroadPaper templates.";
/** The one tool the model is given. */
export const TOOL_NAME = "propose_template";
